<template>
  <v-card flat class="external-product-images-credentials">
    <v-card-title>Credenciales de imágenes de productos</v-card-title>
    <v-card-text>
      <v-alert v-if="configuration && configuration.status === 'INCOMPLETE'" type="warning" dense>
        La configuración está incompleta. Debe reemplazar las credenciales eliminadas antes de probar o enviar.
      </v-alert>
      <v-form @submit.prevent="saveConfiguration">
        <v-row>
          <v-col cols="12" md="6">
            <v-text-field v-model="form.region" label="Región" placeholder="Ej. us-east-1" outlined dense required />
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field v-model="form.bucket" label="Bucket" placeholder="Ej. productos-mi-negocio" outlined dense required />
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field v-model="form.prefix" label="Prefix" placeholder="Ej. productos/2026/" outlined dense required />
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field v-model="form.apiBaseUrl" label="URL base del API" placeholder="Ej. https://api.ejemplo.com" outlined dense required />
          </v-col>
        </v-row>
        <v-divider class="my-4" />
        <div class="subtitle-1 mb-2">Access Key ID</div>
        <v-radio-group v-model="form.accessKeyIdMode" row>
          <v-radio label="Conservar actual" value="KEEP" :disabled="!configuration || !configuration.secrets.accessKeyIdConfigured" />
          <v-radio label="Reemplazar" value="REPLACE" />
          <v-radio label="Eliminar" value="CLEAR" :disabled="!configuration || !configuration.secrets.accessKeyIdConfigured" />
        </v-radio-group>
        <v-text-field v-if="form.accessKeyIdMode === 'REPLACE'" v-model="form.accessKeyId" label="Nuevo Access Key ID" placeholder="Ej. AKIAIOSFODNN7EXAMPLE" type="password" outlined dense autocomplete="new-password" />
        <div class="subtitle-1 mb-2 mt-4">Secret Access Key</div>
        <v-radio-group v-model="form.secretAccessKeyMode" row>
          <v-radio label="Conservar actual" value="KEEP" :disabled="!configuration || !configuration.secrets.secretAccessKeyConfigured" />
          <v-radio label="Reemplazar" value="REPLACE" />
          <v-radio label="Eliminar" value="CLEAR" :disabled="!configuration || !configuration.secrets.secretAccessKeyConfigured" />
        </v-radio-group>
        <v-text-field v-if="form.secretAccessKeyMode === 'REPLACE'" v-model="form.secretAccessKey" label="Nuevo Secret Access Key" placeholder="Ej. wJalrXUtnFEMI/K7MDENG/bPxRfiCYEXAMPLEKEY" type="password" outlined dense autocomplete="new-password" />
        <div class="subtitle-1 mb-2 mt-4">API Key</div>
        <v-radio-group v-model="form.apiKeyMode" row>
          <v-radio label="Conservar actual" value="KEEP" :disabled="!configuration || !configuration.secrets.apiKeyConfigured" />
          <v-radio label="Reemplazar" value="REPLACE" />
          <v-radio label="Eliminar" value="CLEAR" :disabled="!configuration || !configuration.secrets.apiKeyConfigured" />
        </v-radio-group>
        <v-text-field v-if="form.apiKeyMode === 'REPLACE'" v-model="form.apiKey" label="Nueva API Key" placeholder="Ej. api_1234567890abcdef" type="password" outlined dense autocomplete="new-password" />
        <v-alert v-if="configuration" type="info" dense>
          Estado: {{ configuration.status }}. Los secretos nunca se precargan ni se muestran en esta pantalla.
        </v-alert>
        <v-btn type="submit" color="primary" :loading="saving">Guardar configuración</v-btn>
        <v-btn class="ml-2" color="secondary" :loading="testing" :disabled="!configuration || configuration.status !== 'ACTIVE'" @click="testConnection">
          Probar /ping
        </v-btn>
        <v-btn v-if="configuration && configuration.status !== 'DELETED'" class="ml-2" color="error" text :loading="deleting" @click="deleteConfiguration">
          Eliminar configuración
        </v-btn>
      </v-form>
    </v-card-text>
  </v-card>
</template>

<script>
const emptyForm = () => ({
  region: '',
  bucket: '',
  prefix: '',
  apiBaseUrl: '',
  accessKeyId: '',
  secretAccessKey: '',
  apiKey: '',
  accessKeyIdMode: 'REPLACE',
  secretAccessKeyMode: 'REPLACE',
  apiKeyMode: 'REPLACE'
});

export default {
  name: 'ManageExternalProductImagesCredentials',
  data() {
    return {
      configuration: null,
      form: emptyForm(),
      saving: false,
      testing: false,
      deleting: false
    };
  },
  mounted() {
    this.loadConfiguration();
  },
  methods: {
    errorMessage(error, fallback) {
      return error?.response?.data?.message || error?.message || fallback;
    },
    async loadConfiguration() {
      try {
        const response = await this.$API.externalProductImages.getConfiguration();
        this.configuration = response.data || null;
        if (this.configuration) {
          this.form.region = this.configuration.region || '';
          this.form.bucket = this.configuration.bucket || '';
          this.form.prefix = this.configuration.prefix || '';
          this.form.apiBaseUrl = this.configuration.apiBaseUrl || '';
          this.form.accessKeyIdMode = this.configuration.secrets.accessKeyIdConfigured ? 'KEEP' : 'REPLACE';
          this.form.secretAccessKeyMode = this.configuration.secrets.secretAccessKeyConfigured ? 'KEEP' : 'REPLACE';
          this.form.apiKeyMode = this.configuration.secrets.apiKeyConfigured ? 'KEEP' : 'REPLACE';
        }
      } catch (error) {
        this.$toasted.error(this.errorMessage(error, 'No se pudo cargar la configuración'));
      }
    },
    secretPayload(mode, value) {
      return { mode, value: mode === 'REPLACE' ? value : null };
    },
    buildPayload() {
      const payload = {
        region: this.form.region,
        bucket: this.form.bucket,
        prefix: this.form.prefix,
        apiBaseUrl: this.form.apiBaseUrl
      };
      if (!this.configuration) {
        payload.accessKeyId = this.form.accessKeyId;
        payload.secretAccessKey = this.form.secretAccessKey;
        payload.apiKey = this.form.apiKey;
        return payload;
      }
      payload.accessKeyId = this.secretPayload(this.form.accessKeyIdMode, this.form.accessKeyId);
      payload.secretAccessKey = this.secretPayload(this.form.secretAccessKeyMode, this.form.secretAccessKey);
      payload.apiKey = this.secretPayload(this.form.apiKeyMode, this.form.apiKey);
      return payload;
    },
    async saveConfiguration() {
      this.saving = true;
      try {
        const response = this.configuration
          ? await this.$API.externalProductImages.updateConfiguration(this.buildPayload())
          : await this.$API.externalProductImages.createConfiguration(this.buildPayload());
        this.configuration = response.data;
        this.form.accessKeyId = '';
        this.form.secretAccessKey = '';
        this.form.apiKey = '';
        this.form.accessKeyIdMode = 'KEEP';
        this.form.secretAccessKeyMode = 'KEEP';
        this.form.apiKeyMode = 'KEEP';
        this.$toasted.success(response.message || 'Configuración guardada');
      } catch (error) {
        this.$toasted.error(this.errorMessage(error, 'No se pudo guardar la configuración'));
      } finally {
        this.saving = false;
      }
    },
    async testConnection() {
      this.testing = true;
      try {
        const response = await this.$API.externalProductImages.pingConfiguration();
        const message = response.data?.message || response.message || 'La prueba terminó';
        response.success ? this.$toasted.success(message) : this.$toasted.error(message);
      } catch (error) {
        this.$toasted.error(this.errorMessage(error, 'No se pudo probar la conexión'));
      } finally {
        this.testing = false;
      }
    },
    async deleteConfiguration() {
      if (!window.confirm('¿Desea eliminar lógicamente esta configuración? El historial se conservará.')) return;
      this.deleting = true;
      try {
        const response = await this.$API.externalProductImages.deleteConfiguration();
        this.$toasted.success(response.message || 'Configuración eliminada');
        await this.loadConfiguration();
      } catch (error) {
        this.$toasted.error(this.errorMessage(error, 'No se pudo eliminar la configuración'));
      } finally {
        this.deleting = false;
      }
    }
  }
};
</script>

<style scoped>
.external-product-images-credentials {
  background-color: #ffffff !important;
}
</style>
